// What the funnel's hover card SAYS. Pure: hover target + the funnel's own
// stages/edges in, a title and value/label rows out, so a test can pin which
// denominator each percentage is over. `components/chart-card.tsx` renders it.
//
// Only what `FunnelDisplay` names is available: reached/stalled per stage, the
// per-edge count, and each one's secondary split. A row that needs more means
// adding a column to `FunnelDisplay` first.

import { formatPct, sharePct } from "@bi/shared";
import type { FunnelHoverTarget, FunnelStageDatum, FunnelTransitionDatum } from "@bi/shared";

export interface FunnelTipRow {
  value: string;
  label: string;
}

export interface FunnelTip {
  title: string;
  rows: FunnelTipRow[];
}

export interface FunnelTipContext {
  stages: readonly FunnelStageDatum[];
  transitions: readonly FunnelTransitionDatum[];
  /** Plural noun for one unit of this funnel, from `FunnelDisplay.unit_label`
   *  — "projects", "sellers", "briefs". The rows read as sentences, so the
   *  noun is not decoration. */
  unitLabel: string;
  /** Names the hatched sub-series, from `FunnelDisplay.secondary_series_label`
   *  — "guest". `null` when this funnel has no sub-series, and then no row
   *  mentions one: a split of "80 / 0" on a single-series funnel describes a
   *  division that does not exist. */
  secondaryLabel: string | null;
  /** Renders one figure at FULL precision (the card has room, unlike a bar
   *  label). Supplied by the caller so the money funnel's dollars and the
   *  count funnel's integers each read correctly. */
  formatValue: (value: number) => string;
}

/** The hatched sub-series as its own row: the secondary figure and its share
 *  of the total beside it.
 *
 *  Stated as "N of these are <label>" rather than as the admin page's paired
 *  "signed in / guest", because only the admin page knows what the OTHER half
 *  is called. Here the sub-series has an authored name and its complement has
 *  none, so naming one and implying the other is the honest shape. */
function splitRow(total: number, secondary: number, ctx: FunnelTipContext): FunnelTipRow | null {
  if (ctx.secondaryLabel === null || secondary <= 0) return null;
  return {
    value: ctx.formatValue(secondary),
    label: `of these are ${ctx.secondaryLabel}, ${formatPct(sharePct(secondary, total))}`,
  };
}

function pushRow(rows: FunnelTipRow[], row: FunnelTipRow | null): void {
  if (row !== null) rows.push(row);
}

/**
 * The card for one hover target, or `null` when the target names a stage this
 * funnel does not have (a defensive case: the chart derives its targets from
 * the same stage list, so it should not arise).
 *
 * The four denominators, stated once here because each is a different question
 * and using the wrong one is the classic funnel lie:
 *
 * - a NODE's share is over the COHORT (stage 0's `reached`) — "how much of
 *   everything that entered got this far"
 * - a node's step share is over the PREVIOUS stage — "how much of the rung
 *   below made it up"
 * - a DROP's share is over its OWN stage's `reached` — "of everything that got
 *   here, how much stopped here"
 * - a BAND's share is over its FROM stage's `reached` — same question, asked of
 *   the ones that continued
 */
export function funnelTip(target: FunnelHoverTarget, ctx: FunnelTipContext): FunnelTip | null {
  const { stages, unitLabel, formatValue } = ctx;
  const cohort = stages[0]?.reached ?? 0;
  const indexOf = (key: string) => stages.findIndex((s) => s.key === key);
  const labelOf = (key: string) => stages.find((s) => s.key === key)?.label ?? key;

  if (target.kind === "node") {
    const index = indexOf(target.key);
    const stage = stages[index];
    if (!stage) return null;
    const rows: FunnelTipRow[] = [
      {
        value: formatValue(stage.reached),
        label:
          index === 0
            ? `${unitLabel} in this cohort`
            : `reached, ${formatPct(sharePct(stage.reached, cohort))} of the cohort`,
      },
    ];
    const previous = index > 0 ? stages[index - 1] : undefined;
    if (previous) {
      rows.push({
        value: formatPct(sharePct(stage.reached, previous.reached)),
        label: `of "${previous.label}" made it here`,
      });
    }
    pushRow(rows, splitRow(stage.reached, stage.guestReached, ctx));
    if (stage.stalled > 0) {
      rows.push({
        value: formatValue(stage.stalled),
        label:
          index === stages.length - 1
            ? "ended here, which is the win"
            : `stopped here, ${formatPct(sharePct(stage.stalled, stage.reached))} of this stage`,
      });
    }
    return { title: stage.label, rows };
  }

  if (target.kind === "drop") {
    const stage = stages[indexOf(target.key)];
    if (!stage) return null;
    const rows: FunnelTipRow[] = [
      {
        value: formatValue(target.stalled),
        label: `${unitLabel} got no further than this`,
      },
      {
        value: formatPct(sharePct(target.stalled, stage.reached)),
        label: "of everything that reached this stage",
      },
      {
        value: formatPct(sharePct(target.stalled, cohort)),
        label: "of the whole cohort",
      },
    ];
    pushRow(rows, splitRow(stage.stalled, stage.guestStalled, ctx));
    return { title: `Stopped at "${stage.label}"`, rows };
  }

  // band and bypass share their arithmetic: both are an edge measured against
  // the stage it leaves. Only the wording differs, because a bypass is the
  // edge that SKIPS a column and that is the whole reason it is drawn apart.
  const from = stages[indexOf(target.fromKey)];
  const rows: FunnelTipRow[] = [
    {
      value: formatValue(target.count),
      label:
        from === undefined
          ? `${unitLabel} made this step`
          : `${unitLabel} made this step, ${formatPct(sharePct(target.count, from.reached))} of "${from.label}"`,
    },
  ];
  pushRow(rows, splitRow(target.count, target.secondaryCount, ctx));
  const title = `${labelOf(target.fromKey)} to ${labelOf(target.toKey)}`;
  if (target.kind === "bypass") {
    rows.push({
      value: formatPct(sharePct(target.count, cohort)),
      label: "of the whole cohort",
    });
    return { title: `${title}, skipping a stage`, rows };
  }
  return { title, rows };
}
